<script setup lang="ts">
import { get, has } from 'json-pointer'
import { useClaimLab } from '../stores/claimLab'
import type { RawInsight } from '../types'
import StatusInsights from './StatusInsights.vue'

const props = defineProps<{
  activeSequence: number | undefined
  isConsoleUser?: boolean
}>()

const claimLab = useClaimLab()
const allInsights = computed(() =>
  props.activeSequence &&
  Boolean(claimLab.testResult) &&
  claimLab.nonActionableInsightsBySequence?.has(props.activeSequence)
    ? claimLab.nonActionableInsightsBySequence.get(props.activeSequence)!
    : [],
)
const searchTerm = ref('')

const searchPaths = [
  '/insight/policy_id',
  '/insight/paapi_policy_name',
  '/insight/insight/type',
]

const matches = (insight: RawInsight, words: string[]) =>
  searchPaths.some((path) => {
    const value = has(insight, path) ? get(insight, path) : undefined

    return (
      typeof value === 'string' &&
      words.every((word) => value.toLowerCase().includes(word))
    )
  })

const insights = computed(() => {
  const words = searchTerm.value.toLowerCase().split(' ').filter(Boolean)

  return words.length
    ? allInsights.value.filter((insight) => matches(insight, words))
    : allInsights.value
})
</script>

<template>
  <UCollapsible class="w-full border-t border-neutral-300">
    <UButton
      class="w-full justify-between px-4 py-3"
      color="neutral"
      trailing-icon="i-ph-caret-down"
      variant="ghost"
    >
      <h3>
        Non-Actionable Insights ({{
          activeSequence && claimLab.testResult
            ? `${claimLab.nonActionableInsightsBySequence?.get(activeSequence)?.length || 0}`
            : 'Run test to view'
        }})
      </h3>
    </UButton>
    <template #content>
      <div class="max-h-[500px] overflow-y-scroll p-4">
        <div v-if="!allInsights.length">No Insights</div>
        <template v-else>
          <UInput
            v-model="searchTerm"
            class="mb-4 w-full"
            icon="i-ph-magnifying-glass"
            placeholder="Search by insight type, policy name or policy ID"
          />
          <StatusInsights
            v-if="insights.length"
            :insights="insights"
            :is-console-user="isConsoleUser"
          />
          <div v-else class="text-sm">No matching insights found</div>
        </template>
      </div>
    </template>
  </UCollapsible>
</template>
